"use client";

import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { supabase } from "@/lib/supabaseClient";
import {
  profileCompletionRedirectUrl,
  shouldBlockAssessmentAccess,
  PROFILE_GUARD_SELECT,
} from "@/lib/profileCompleteness";

type AnimatedSectionProps = {
  id?: string;
  className?: string;
  children: ReactNode;
};

function cn(...classes: Array<string | null | undefined | false>) {
  return classes.filter(Boolean).join(" ");
}

function useInView(threshold = 0.2) {
  const [isVisible, setIsVisible] = useState(false);
  const [ref, setRef] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(ref);

    return () => observer.disconnect();
  }, [ref, threshold]);

  return { ref: setRef, isVisible };
}

function AnimatedSection({ id, className, children }: AnimatedSectionProps) {
  const { ref, isVisible } = useInView();

  return (
    <section
      id={id}
      ref={ref}
      className={cn(
        "py-16 sm:py-20 transition-all duration-700 ease-out",
        "opacity-0 translate-y-6",
        isVisible && "opacity-100 translate-y-0",
        className,
      )}
    >
      {children}
    </section>
  );
}

export default function HomePageClient() {
  const router = useRouter();
  const [paidPriceInr, setPaidPriceInr] = useState<number>(999);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/pricing", { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as { priceInr?: unknown };
        const n = typeof data.priceInr === "number" ? data.priceInr : null;
        if (!cancelled && n != null && Number.isFinite(n) && n > 0) {
          setPaidPriceInr(Math.round(n));
        }
      } catch {
        // keep default
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const faqs = [
    {
      question: "Who is Aptinomic for?",
      answer:
        "Aptinomic is built for Indian students from Class 10 onwards, college graduates, and early professionals who want clarity on what to do next.",
    },
    {
      question: "Is the guidance really India-specific?",
      answer:
        "Yes. Our AI is trained on Indian entrance exams, colleges, government and private career paths, and real job market data across metros and Tier-2/3 cities.",
    },
    {
      question: "How accurate is the AI assessment?",
      answer:
        "Your report blends psychometrics, interest mapping, and skills analysis. It is not a guarantee, but a high-quality decision-support tool you can revisit as you grow.",
    },
    {
      question: "Do I need to talk to a human counselor?",
      answer:
        "You can use Aptinomic fully self-service. For deeper questions, you can share your Aptinomic report with parents, mentors, or a counselor for richer conversations.",
    },
    {
      question: "Will my data be kept private?",
      answer:
        "Absolutely. Your responses and reports are stored securely, never sold, and only used to improve your own experience on Aptinomic.",
    },
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const handleStartAssessment = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      router.push("/login?redirect=/assessment");
      return;
    }
    const [{ data: profile }, { data: anyAssessment }] = await Promise.all([
      supabase
        .from("profiles")
        .select(PROFILE_GUARD_SELECT)
        .eq("id", user.id)
        .maybeSingle(),
      supabase
        .from("assessments")
        .select("id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle(),
    ]);
    if (shouldBlockAssessmentAccess(profile, Boolean(anyAssessment))) {
      router.push(profileCompletionRedirectUrl("/assessment"));
      return;
    }
    router.push("/assessment");
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900 text-slate-100">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="apt-bg-blob apt-bg-blob--indigo" />
        <div className="apt-bg-blob apt-bg-blob--amber" />
        <div className="apt-bg-grid" />
      </div>

      <main id="top" className="px-4 pb-20 pt-6 sm:px-6 sm:pb-28 sm:pt-10">
        <div className="mx-auto max-w-6xl space-y-16 sm:space-y-24">
          {/* Hero */}
          <AnimatedSection id="hero" className="pt-4 sm:pt-6">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-center">
              <div className="space-y-6">
                <span className="inline-flex items-center gap-2 rounded-full bg-indigo-500/15 px-3 py-1 text-xs font-semibold text-indigo-100 ring-1 ring-indigo-500/40">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-300" />
                  Made for Indian students & professionals
                </span>
                <h1 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
                  Find Your Perfect Career Path with AI
                </h1>
                <p className="max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
                  Aptinomic combines a structured 5-layer assessment with a guided
                  career dialogue—then generates a premium, actionable report that
                  helps you choose with confidence (and reduces the risk of wrong
                  turns).
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
                  <button
                    type="button"
                    onClick={() => void handleStartAssessment()}
                    className="inline-flex items-center justify-center rounded-full bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-indigo-900/60 transition hover:bg-indigo-400"
                    aria-label="Start free career assessment"
                  >
                    Start Free Assessment
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById("how-it-works");
                      el?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="inline-flex items-center justify-center rounded-full border border-slate-600 bg-slate-900/60 px-5 py-3 text-sm font-medium text-slate-200/80 shadow-sm transition hover:border-indigo-400 hover:text-indigo-300"
                    aria-label="Scroll to how it works on this page"
                  >
                    See how it works
                  </button>
                </div>
                <p className="text-xs text-slate-400">
                  <Link
                    href="/how-it-works"
                    className="font-medium text-indigo-300 underline-offset-2 hover:text-indigo-200 hover:underline"
                  >
                    Read the full &quot;How it works&quot; guide
                  </Link>
                  {" · "}
                  <Link href="/contact" className="text-slate-400 underline-offset-2 hover:text-slate-300 hover:underline">
                    Contact
                  </Link>
                </p>
                <p className="text-xs text-slate-500">
                  No credit card to start. Complete a 15-minute assessment, then
                  generate your premium report from your dashboard.
                </p>
              </div>

              {/* Report journey preview widget */}
              <div className="relative">
                <div className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-indigo-500/40 via-transparent to-amber-300/20 blur-3xl" />
                <div className="rounded-3xl bg-slate-900/80 p-5 shadow-xl shadow-indigo-900/40 ring-1 ring-indigo-500/40 backdrop-blur">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-indigo-200">
                        Report Journey
                      </p>
                      <p className="text-sm font-semibold text-slate-50">
                        From assessment to premium guidance
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/15 px-3 py-1 text-[11px] font-semibold text-indigo-100">
                      <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                      Dashboard-based flow
                    </span>
                  </div>

                  <div className="mb-4 grid grid-cols-4 gap-2 text-[10px] text-slate-200">
                    {[
                      { label: "Personality", value: 100, color: "bg-indigo-400" },
                      { label: "Interests", value: 100, color: "bg-emerald-400" },
                      { label: "Thinking", value: 100, color: "bg-sky-400" },
                      { label: "EQ", value: 100, color: "bg-amber-300" },
                    ].map((layer) => (
                      <div key={layer.label} className="space-y-1">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                          <div
                            className={cn("h-full rounded-full", layer.color)}
                            style={{ width: `${layer.value}%` }}
                          />
                        </div>
                        <p className="truncate text-[10px] text-slate-300">
                          {layer.label}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mb-4 flex items-center justify-between text-[11px] text-slate-300/80">
                    <span>Career dialogue adds life context</span>
                    <span className="font-medium text-emerald-300">
                      Ready for Step 3
                    </span>
                  </div>

                  <div className="grid gap-3 text-xs sm:grid-cols-3">
                    <div className="rounded-2xl bg-indigo-500/20 p-3 ring-1 ring-indigo-500/40">
                      <p className="text-[11px] font-semibold text-indigo-50">
                        Step 1
                      </p>
                      <p className="mt-1 text-[11px] text-slate-200/80">
                        Complete 5-layer assessment
                      </p>
                    </div>
                    <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/60 p-3">
                      <p className="text-[11px] font-semibold text-slate-50">
                        Step 2
                      </p>
                      <p className="mt-1 text-[11px] text-slate-200/80">
                        Career dialogue insights
                      </p>
                    </div>
                    <div className="rounded-2xl bg-slate-900/80 p-3 ring-1 ring-amber-300/40">
                      <p className="text-[11px] font-semibold text-slate-50">
                        Step 3
                      </p>
                      <p className="mt-1 text-[11px] text-slate-200/80">
                        Generate report, then view
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Trust Section */}
          <AnimatedSection id="trust">
            <div className="space-y-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-300">
                Trust
              </p>
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
                    Trusted by 10,000+ Indian students
                  </h2>
                  <p className="mt-2 max-w-xl text-sm text-slate-300">
                    From CBSE and ICSE schools to state boards and engineering colleges,
                    students across India use Aptinomic to make calmer, better informed
                    career decisions.
                  </p>
                </div>
                <div className="inline-flex items-center gap-2 rounded-2xl bg-slate-900/80 px-4 py-3 text-xs text-slate-100 shadow-sm ring-1 ring-slate-700">
                  <span className="text-lg text-amber-300">★</span>
                  <div>
                    <p className="font-semibold">
                      4.8/5 average satisfaction
                    </p>
                    <p className="text-[11px] text-slate-300">
                      Based on anonymised feedback
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  {
                    name: "Aarya, Class 11 – Pune",
                    quote:
                      "I was stuck between medicine and design. The report showed why I lean towards human-centered work, not just marks.",
                  },
                  {
                    name: "Rohit, B.Tech – Lucknow",
                    quote:
                      "Aptinomic mapped my interest in problem solving to real roles beyond just ‘software engineer’. The AI interview felt like a mentor.",
                  },
                  {
                    name: "Sara, Career switch – Bengaluru",
                    quote:
                      "After three years in support, I finally have a practical plan to move into product without starting from zero.",
                  },
                ].map((t, index) => (
                  <figure
                    key={t.name}
                    className={cn(
                      "flex h-full flex-col justify-between rounded-3xl bg-slate-900/80 p-4 text-sm shadow-sm ring-1 ring-slate-800",
                      index === 1 &&
                        "bg-gradient-to-b from-indigo-500/20 via-slate-900/90 to-slate-900/90 ring-indigo-500/60",
                    )}
                  >
                    <p className="text-[13px] leading-relaxed text-slate-200">
                      “{t.quote}”
                    </p>
                    <figcaption className="mt-4 text-xs font-semibold text-slate-100">
                      {t.name}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* How It Works */}
          <AnimatedSection id="how-it-works">
            <div className="space-y-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-300">
                How it works
              </p>
              <h2 className="font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
                In four structured steps
              </h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    title: "Complete the 5-layer assessment",
                    desc: "A guided, 15-minute assessment across psychology, interests, thinking style, emotional intelligence, and practical career context.",
                  },
                  {
                    title: "Career dialogue insights",
                    desc: "Your responses are analysed through an AI conversation so the system understands what you want, how you decide, and what you value.",
                  },
                  {
                    title: "Generate your report (Dashboard Step 3)",
                    desc: "After your dialogues, you’ll build your layered report from Step 3 in your dashboard. It runs layer-by-layer on the server.",
                  },
                  {
                    title: "View guidance + roadmap",
                    desc: "Free gives you a premium teaser. Paid unlocks the full career guidance report, including skill gaps and a practical roadmap.",
                  },
                ].map((step, index) => (
                  <div
                    key={step.title}
                    className="flex h-full flex-col gap-3 rounded-3xl bg-slate-900/80 p-4 shadow-sm ring-1 ring-slate-800"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-indigo-500/15 text-sm font-semibold text-amber-300">
                        {index + 1}
                      </div>
                      <h3 className="text-sm font-semibold text-slate-50">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-xs leading-relaxed text-slate-300">
                      {step.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* What We Analyze */}
          <AnimatedSection id="what-we-analyze">
            <div className="space-y-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-300">
                What we analyze
              </p>
              <h2 className="font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
                5 layers of career insight
              </h2>
              <p className="max-w-2xl text-sm leading-relaxed text-slate-300">
                Aptinomic doesn’t rely on one score. It blends psychology, interests, thinking style, emotional intelligence, and practical career context so your report feels more “you”.
              </p>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {[
                  {
                    title: "Personality (OCEAN)",
                    desc: "How you naturally think, feel, and behave—so fit recommendations are more realistic.",
                  },
                  {
                    title: "Interests (RIASEC)",
                    desc: "The tasks you’ll likely enjoy, mapped to career environments.",
                  },
                  {
                    title: "Thinking & problem solving",
                    desc: "Cognitive style and learning tendencies that shape how you work and grow.",
                  },
                  {
                    title: "Emotional intelligence (EQ)",
                    desc: "Motivation, stress handling, and interpersonal patterns for long-term consistency.",
                  },
                  {
                    title: "Career dialogue",
                    desc: "A guided conversation that turns your aspirations and constraints into actionable signals.",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="flex h-full flex-col justify-between rounded-3xl bg-slate-900/80 p-4 shadow-sm ring-1 ring-slate-800"
                  >
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-slate-50">{item.title}</h3>
                      <p className="text-xs leading-relaxed text-slate-300">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* What You'll Get */}
          <AnimatedSection id="what-you-get">
            <div className="space-y-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-300">
                Report preview
              </p>
              <h2 className="font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
                What your full career report includes
              </h2>
              <p className="max-w-2xl text-sm leading-relaxed text-slate-300">
                Free gives you selected highlights. Paid unlocks the complete, structured guidance report and roadmap.
              </p>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {[
                  {
                    title: "Career identity & best-fit paths",
                    desc: "A clear starting point and direction based on your assessment + dialogue. Paid expands this into multiple realistic routes.",
                  },
                  {
                    title: "Strengths & blind spots",
                    desc: "Narratives that explain what you’re naturally strong at—and where you’ll need extra support. Paid adds practical examples.",
                  },
                  {
                    title: "Skill gaps & readiness",
                    desc: "What you may be missing today (and what to do next) so your effort aligns with real outcomes. Paid unlocks the full skill plan.",
                  },
                  {
                    title: "Practical roadmap",
                    desc: "A structured timeline of what to do now—projects, skills, and next decisions. Paid includes exam/college pathway guidance.",
                  },
                  {
                    title: "Family-ready report access",
                    desc: "A premium teaser in free; paid unlocks the full structured report and shareable PDF for parents & mentors.",
                  },
                ].map((item, idx) => (
                  <div
                    key={item.title}
                    className={cn(
                      "flex h-full flex-col justify-between rounded-3xl bg-slate-900/80 p-4 shadow-sm ring-1 ring-slate-800",
                      idx === 0 && "md:col-span-2 lg:col-span-1",
                    )}
                  >
                    <div className="mb-3 inline-flex items-center gap-2 text-[11px] text-indigo-200">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-300" />
                      Included in paid report
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-slate-50">
                        {item.title}
                      </h3>
                      <p className="text-xs leading-relaxed text-slate-300">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* Pricing */}
          <AnimatedSection id="pricing">
            <div className="space-y-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-300">
                Pricing
              </p>
              <h2 className="font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
                Start free. Upgrade when you&apos;re ready.
              </h2>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="flex h-full flex-col justify-between rounded-3xl bg-slate-900/80 p-5 shadow-sm ring-1 ring-slate-800">
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                      Free
                    </p>
                    <h3 className="text-sm font-semibold text-slate-50">
                      Explore your options
                    </h3>
                    <p className="flex items-baseline gap-1">
                      <span className="text-2xl font-semibold text-slate-50">
                        ₹0
                      </span>
                      <span className="text-xs text-slate-300">
                        for students & parents
                      </span>
                    </p>
                    <ul className="mt-2 space-y-1.5 text-xs text-slate-300">
                      <li>• Complete the full 5-layer assessment</li>
                      <li>• Get a premium teaser of your guidance</li>
                      <li>• Dialogue highlights that inform your report</li>
                      <li>• Generate your report anytime from Step 3</li>
                    </ul>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleStartAssessment()}
                    className="mt-4 w-full rounded-full border border-slate-600 bg-slate-950/70 px-4 py-2.5 text-xs font-semibold text-slate-200/80 transition hover:border-indigo-400 hover:text-indigo-300"
                  >
                    Start free assessment
                  </button>
                </div>

                <div className="relative flex h-full flex-col justify-between rounded-3xl bg-gradient-to-b from-indigo-500 via-indigo-600 to-indigo-900 p-5 text-white shadow-lg shadow-indigo-900/60">
                  <div className="absolute right-4 top-4 rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/90">
                    Most chosen
                  </div>
                  <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/80">
                      Complete report
                    </p>
                    <h3 className="text-sm font-semibold">Lifetime access</h3>
                    <p className="flex items-baseline gap-1">
                      <span className="text-2xl font-semibold">
                        ₹{paidPriceInr.toLocaleString("en-IN")}
                      </span>
                      <span className="text-xs text-white/80">one-time</span>
                    </p>
                    <ul className="mt-2 space-y-1.5 text-xs text-white/90">
                      <li>• Full layered career guidance report (all sections)</li>
                      <li>• Skill gaps + readiness plan built from your inputs</li>
                      <li>• Practical roadmap and decision guidance</li>
                      <li>• Shareable PDF for parents & mentors</li>
                    </ul>
                  </div>
                  <Link
                    href="/checkout"
                    className="mt-4 block w-full rounded-full bg-white px-4 py-2.5 text-center text-xs font-semibold text-slate-950 shadow-sm transition hover:bg-slate-100"
                  >
                    Unlock full report
                  </Link>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* FAQ */}
          <AnimatedSection id="faq">
            <div className="space-y-6">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-300">
                FAQ
              </p>
              <h2 className="font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
                Questions students & parents often ask
              </h2>

              <div className="space-y-3 rounded-3xl bg-slate-900/80 p-4 shadow-sm ring-1 ring-slate-800 sm:p-5">
                {faqs.map((faq, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div
                      key={faq.question}
                      className={cn(
                        "rounded-2xl border border-transparent bg-slate-950/60 px-3 py-2.5 transition",
                        isOpen && "border-indigo-400/80 bg-indigo-500/10",
                      )}
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-4 text-left"
                        onClick={() =>
                          setOpenFaqIndex((prev) => (prev === index ? null : index))
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="text-sm font-semibold text-slate-50">
                          {faq.question}
                        </span>
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-xs font-semibold text-amber-300">
                          {isOpen ? "–" : "+"}
                        </span>
                      </button>
                      {isOpen && (
                        <p className="mt-2.5 text-xs leading-relaxed text-slate-300">
                          {faq.answer}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </AnimatedSection>

          {/* Final CTA */}
          <AnimatedSection id="final-cta">
            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 shadow-sm shadow-slate-950/30 sm:p-8">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-300">
                    Next step
                  </p>
                  <h2 className="font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
                    Start free, then unlock the full guidance
                  </h2>
                  <p className="max-w-xl text-sm leading-relaxed text-slate-300">
                    Once you complete the 5-layer assessment, you can generate your premium report from Step 3 in your dashboard.
                  </p>
                </div>
                <div className="flex flex-col gap-3 sm:items-end">
                  <Link
                    href="/assessment"
                    className="inline-flex items-center justify-center rounded-full bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-indigo-900/60 transition hover:bg-indigo-400"
                  >
                    Start free assessment
                  </Link>
                  <Link
                    href="/checkout"
                    className="inline-flex items-center justify-center rounded-full border border-slate-600 bg-slate-900/60 px-6 py-3 text-sm font-medium text-slate-200/80 shadow-sm transition hover:border-indigo-400 hover:text-indigo-300"
                  >
                    Unlock full report (₹{paidPriceInr.toLocaleString("en-IN")})
                  </Link>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </main>
    </div>
  );
}
